import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BooksGrid from './BooksGrid';
import SearchBar from './SearchBar';
import RecentListens from './RecentListens';
import PlanTab from './PlanTab';
import Settings from './Settings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCog } from '@fortawesome/free-solid-svg-icons';

function NavigationPopover({ isOpen, onClose, onLogout, isOnline, defaultTab = 'books' }) {
  const [selectedBook, setSelectedBook] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isTrackingMode, setIsTrackingMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const popoverStateRef = useRef({ isOpen: false, selectedBook: null, showSettings: false });

  // Update tab when defaultTab changes (e.g. after async DB check)
  useEffect(() => {
    if (isOpen) {
        setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  // Helper function to check if navigation state exists in history
  const hasNavigationState = () => {
    return window.history.state?.navigationPopover !== undefined;
  };

  // Helper function to check if navigation state has changed
  const isNavigationStateChanged = (currentNavState, newShowSettings, newSelectedBook) => {
    if (!currentNavState) return true;
    if (currentNavState.showSettings !== newShowSettings) return true;
    // Compare book IDs, handling null/undefined cases
    const currentBookId = currentNavState.selectedBook?.id ?? null;
    const newBookId = newSelectedBook?.id ?? null;
    if (currentBookId !== newBookId) return true;
    return false;
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      // Only handle popstate if the navigation popover is open
      if (!popoverStateRef.current.isOpen) return;

      if (event.state?.navigationPopover) {
        const { showSettings: historyShowSettings, selectedBook: historySelectedBook } = event.state.navigationPopover;
        
        // Update state based on history
        setShowSettings(historyShowSettings || false);
        setSelectedBook(historySelectedBook || null);
      } else {
        // No navigation state in history, close the popover
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onClose]);

  // Manage history when navigation state changes
  useEffect(() => {
    popoverStateRef.current = { isOpen, selectedBook, showSettings };

    if (isOpen) {
      const currentState = window.history.state;
      const currentNavState = currentState?.navigationPopover;
      
      // Check if navigation state has actually changed
      if (isNavigationStateChanged(currentNavState, showSettings, selectedBook)) {
        const newState = {
          ...currentState,
          navigationPopover: { showSettings, selectedBook }
        };
        window.history.pushState(newState, '');
      }
    }
  }, [isOpen, selectedBook, showSettings]);

  const handleLogout = async () => {
    try {
      // Only try server logout if online
      if (isOnline) {
        await axios.post('/api/logout', {}, { withCredentials: true });
      }
      // Always clean up local state
      onLogout();
      onClose();
    } catch (error) {
      console.error('Logout failed:', error);
      // Still call onLogout to clear local state even if server request fails
      onLogout();
      onClose();
    }
  };
  
  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    // Use history.back() to go back to the navigation popover
    const navState = window.history.state?.navigationPopover;
    if (hasNavigationState() && navState?.showSettings) {
      window.history.back();
    } else {
      setShowSettings(false);
    }
  };

  const handleSetSelectedBook = (book) => {
    setSelectedBook(book);
  };

  const handleBackToBooks = () => {
    // Use history.back() to go back to the books list
    const navState = window.history.state?.navigationPopover;
    if (hasNavigationState() && navState?.selectedBook) {
      window.history.back();
    } else {
      setSelectedBook(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="navigation-popover" 
      style={{ 
        transform: `translateX(${isOpen ? '0' : '-100%'})`,
        zIndex: 'var(--z-navigation)'
      }}
    >
      {showSettings ? (
        <Settings onClose={handleCloseSettings} isOnline={isOnline} />
      ) : (
        <>
          <div className="nav-header">
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h1 className="nav-header-title">Audio Bible & Tracker</h1>
            <div className="tabs">
              <button
                className={activeTab === 'books' ? 'active' : ''}
                onClick={() => setActiveTab('books')}
              >
                Books
              </button>
              <button
                className={activeTab === 'recent' ? 'active' : ''}
                onClick={() => setActiveTab('recent')}
              >
                Recent
              </button>
              <button
                className={activeTab === 'plan' ? 'active' : ''}
                onClick={() => setActiveTab('plan')}
              >
                Plan
              </button>
            </div>
          </div>
          <div className="nav-content" style={{ overflow: 'hidden' }}>
            {activeTab === 'books' && (
              <>
                <div className="books-search-container">
                  <SearchBar closeMenu={onClose} setSelectedBook={handleSetSelectedBook} />
                </div>
                <BooksGrid 
                  closeMenu={onClose}
                  selectedBook={selectedBook}
                  setSelectedBook={handleSetSelectedBook}
                  onBackToBooks={handleBackToBooks}
                  isTrackingMode={isTrackingMode}
                  setIsTrackingMode={setIsTrackingMode}
                />
              </>
            )}
            {activeTab === 'recent' && <RecentListens closeMenu={onClose} />}
            {activeTab === 'plan' && <PlanTab closeMenu={onClose} />}
          </div>
          <div className="nav-footer">
            {!isOnline && (
              <div className="offline-notice">You are offline</div>
            )}
            <div className="footer-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <button className="logout-button" onClick={handleLogout}>
                {isOnline ? 'Logout' : 'Clear Local Data'}
              </button>
              <button 
                className="settings-icon-button"
                onClick={handleOpenSettings}
                aria-label="Settings"
              >
                <FontAwesomeIcon 
                  icon={faCog} 
                  style={{ 
                    fontSize: '20px',
                    color: 'var(--text-muted)'
                  }} 
                />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NavigationPopover;
